import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import { DisabledFeatureFlagsResponseDto, FeatureFlagDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/useApiClient.util.ts';

type Return = {
  disabledFlags: Set<FeatureFlagDto>;
  isError: boolean;
  isLoading: boolean;
};

export const useGetDisabledFeatureFlags = (): Return => {
  const { featureFlagsApi } = useApiClient();
  const { data, fetchStatus, isError, isLoading } = useQuery<
    DisabledFeatureFlagsResponseDto,
    AxiosError
  >({
    queryFn: async () => {
      const response = await featureFlagsApi.getDisabledFeatureFlags();
      return response?.data;
    },
    queryKey: [QueryKeyEnum.DISABLED_FEATURE_FLAGS],
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: Infinity,
  });

  const disabledFlags = useMemo(
    () => new Set<FeatureFlagDto>(data?.disabledFlags?.map(f => f.flag) ?? []),
    [data],
  );

  return {
    disabledFlags,
    isError,
    isLoading: fetchStatus !== 'idle' && isLoading,
  };
};
